/**
 * The engine state: everything a standby (or a restarted process) needs to
 * continue playout exactly where the active left it (PLAN §7.5, §8.3:
 * "Playhead and engine state (on-air/next, take times, fired event instance
 * IDs, holds/overrides)").
 *
 * It is plain JSON. The definitions (rows) and the studio config are NOT in
 * it: both nodes have those already (replicated rows, the launch snapshot),
 * and every instance is a full copy (§5.2), so the timeline generated from a
 * state is the same on either node. Overrides live on the instances
 * (`userStopped`, `disabled`, adlibbed pieces, a queued adlib part). ACP v1
 * has no hold (§5.3).
 *
 * `seq` increases by one on every change, so the active→standby stream can
 * detect a gap and ask for a snapshot (§8.3).
 */
import { epochMsSchema, ulidSchema } from '@acp/domain/primitives.js';
import { z } from 'zod';
import { enginePartInstanceSchema } from './instances.js';

export const ENGINE_STATE_VERSION = 1;

/** How many fired event ids are kept: far more than any instance lives through between two takes. */
export const FIRED_EVENT_ID_LIMIT = 2000;

/** One finished (or on-air) part instance, as the timings need it. */
export const playedPartSchema = z
  .object({
    partInstanceId: ulidSchema,
    partId: ulidSchema,
    segmentId: ulidSchema,
    takeCount: z.number().int().nonnegative(),
    untimed: z.boolean(),
    /** Reported by the devices, else planned. */
    startedAt: epochMsSchema,
    stoppedAt: epochMsSchema,
  })
  .strict();
export type PlayedPart = z.infer<typeof playedPartSchema>;

export const activationSchema = z
  .object({ activationId: ulidSchema, rehearsal: z.boolean(), activatedAt: epochMsSchema })
  .strict();
export type Activation = z.infer<typeof activationSchema>;

export const autoNextSchema = z
  .object({
    /** When the next part group starts on the timeline: the auto take's time. */
    at: epochMsSchema,
    /** The on-air instance whose end it is; a take of anything else voids it. */
    fromPartInstanceId: ulidSchema,
  })
  .strict();
export type AutoNext = z.infer<typeof autoNextSchema>;

export const engineStateSchema = z
  .object({
    version: z.literal(ENGINE_STATE_VERSION),
    seq: z.number().int().nonnegative(),
    activation: activationSchema.nullable(),
    current: enginePartInstanceSchema.nullable(),
    next: enginePartInstanceSchema.nullable(),
    previous: enginePartInstanceSchema.nullable(),
    /** The operator chose the next part (Sofie's `nextPartInfo.manuallySelected`): a rundown edit does not re-select it. */
    nextSetManually: z.boolean(),
    queuedSegmentId: ulidSchema.nullable(),
    takeCount: z.number().int().nonnegative(),
    lastTakeTime: epochMsSchema.nullable(),
    /** The first timed take of this activation (Sofie's `startedPlayback`). */
    startedPlayback: epochMsSchema.nullable(),
    autoNext: autoNextSchema.nullable(),
    /** Idempotency keys of the fired as-run events (`events.ts`), newest last, bounded. */
    firedEventIds: z.array(z.string().max(128)).max(FIRED_EVENT_ID_LIMIT),
    /** The as-played record of this activation, oldest first. */
    history: z.array(playedPartSchema),
  })
  .strict();
export type EngineState = z.infer<typeof engineStateSchema>;

export function initialState(): EngineState {
  return {
    version: ENGINE_STATE_VERSION,
    seq: 0,
    activation: null,
    current: null,
    next: null,
    previous: null,
    nextSetManually: false,
    queuedSegmentId: null,
    takeCount: 0,
    lastTakeTime: null,
    startedPlayback: null,
    autoNext: null,
    firedEventIds: [],
    history: [],
  };
}

export type ParseStateResult = { readonly ok: true; readonly state: EngineState } | { readonly ok: false; readonly error: string };

/** Parse a snapshot from the wire (another process). Never throws. */
export function parseEngineState(input: unknown): ParseStateResult {
  const result = engineStateSchema.safeParse(input);
  return result.success ? { ok: true, state: result.data } : { ok: false, error: z.prettifyError(result.error) };
}
